import { useToast } from "@/components/ui/use-toast";
import CompraRapidaService, { BuscarCompraDto } from "@/services/CompraRapidaService";
import { MASCARA_CPF } from "@/utils/Constants";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { conformToMask } from "react-text-mask";
import { useAuth } from "@/contexts/AuthProvider";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Button } from "@/components/ui/button";
import { Share2 } from "lucide-react";
import { labelPlural, labelSingularLower } from "@/utils/copyTituloBilhete";


export function DetalhesCompraPage() {
    let { idCompra } = useParams();
    const urlParams = new URLSearchParams(window.location.search);
    const navigate = useNavigate();
    const [compra, setCompra] = useState<BuscarCompraDto>();
    const [loading, setLoading] = useState(true)
    const { toast } = useToast()
    const { isAuthorized, usuario } = useAuth();
    const [compartilhando, setCompartilhando] = useState(false);
    const [exibirModalVersaoLimitada, setExibirModalVersaoLimitada] = useState(false);
    const isAndroid = navigator.userAgent.toLowerCase().includes("android");
    const isInstagram = navigator.userAgent.toLowerCase().includes("instagram")

    const tokenCompraRapida = urlParams.get('tokenCompraRapida');
    const compraRapida = urlParams.get('comprarapida');

    useEffect(() => {
        if (!isAuthorized) {
            if (!tokenCompraRapida || !compraRapida) {
                navigate('/')
                return;
            }

            buscarCompraRapida(tokenCompraRapida);
        } else {
            buscarCompra();
        }
    }, [])

    // Função para converter URL de imagem em File
    async function urlToFile(url: string, filename: string): Promise<File> {
        const response = await fetch(url);
        const blob = await response.blob();
        return new File([blob], filename, { type: blob.type });
    }

    async function handleCompartilharTitulos() {
        if (!compra?.UrlCertificados || compra.UrlCertificados.length === 0) {
            toast({
                variant: 'destructive',
                description: `Nenhum ${labelSingularLower()} encontrado para compartilhar`,
                duration: 3000
            });
            return;
        }

        try {
            setCompartilhando(true);

            if (navigator.userAgent.toLowerCase().includes("instagram")) {
                toggleModalVersaoLimitada();
            }

            // Converter URLs em arquivos
            const files: File[] = [];
            for (let i = 0; i < compra.UrlCertificados.length; i++) {
                const url = compra.UrlCertificados[i];
                const filename = `titulo_${compra.NumeroPedido}_${i + 1}.jpeg`;
                const file = await urlToFile(url, filename);
                files.push(file);
            }

            // Verificar se o navegador suporta compartilhamento de arquivos
            if (navigator.canShare && navigator.canShare({ files })) {
                await navigator.share({
                    files,
                    title: `${labelPlural()} - Compra ${compra.NumeroPedido}`,
                    text: `${labelPlural()} da compra número ${compra.NumeroPedido} do sorteio ${compra.DataSorteio}`,
                });
            } else {
                // Fallback: baixar arquivos individualmente
                files.forEach((file) => {
                    const url = URL.createObjectURL(file);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = file.name;
                    document.body.appendChild(a);
                    a.click();
                    document.body.removeChild(a);
                    URL.revokeObjectURL(url);
                });
            }
        } catch (error: any) {
            if (error.toString().includes('AbortError')) {
                return;
            }

            console.error(`Erro ao compartilhar ${labelPlural().toLowerCase()}:`, error);
            toast({
                variant: 'destructive',
                description: `Erro ao compartilhar ${labelPlural().toLowerCase()}. Tente novamente.`,
                duration: 4000
            });
        } finally {
            setCompartilhando(false)
        }
    }

    async function buscarCompra() {
        try {
            if (!idCompra) {
                return
            }

            const { Data: result } = await CompraRapidaService.buscarCompra(parseInt(idCompra), usuario?.TokenAcesso!);
            setCompra(result);
        } catch (error: any) {
            toast({
                variant: 'destructive',
                description: error,
                duration: 4000
            });
        } finally {
            setLoading(false);
        }
    }

    async function buscarCompraRapida(token: string) {
        try {
            if (!idCompra) {
                return
            }

            const { Data: result } = await CompraRapidaService.buscarCompraRapida(token);
            setCompra(result);
        } catch (error: any) {
            toast({
                variant: 'destructive',
                description: error,
                duration: 4000
            });
        } finally {
            setLoading(false);
        }
    }

    async function toggleModalVersaoLimitada() {
        setExibirModalVersaoLimitada(!exibirModalVersaoLimitada!)
    }

    return (
        loading ? <>
            Carregando...
        </> : (
            <div className="px-3 pt-2">
                <h1 className="text-center text-primary-foreground font-semibold text-xl">
                    Detalhes da Compra
                </h1>

                <p className="text-primary-foreground">
                    <label className="font-semibold">Sorteio: </label> {compra?.DataSorteio}
                </p>
                <p className="text-primary-foreground">
                    <label className="font-semibold">Numero: </label> {compra?.NumeroPedido}
                </p>
                <p className="text-primary-foreground">
                    <label className="font-semibold">Nome: </label> {compra?.Usuario.Nome}
                </p>
                <p className="text-primary-foreground">
                    <label className="font-semibold">CPF: </label> {conformToMask(compra?.Usuario.CPF ?? '', MASCARA_CPF).conformedValue}
                </p>
                <p className="text-primary-foreground">
                    <label className="font-semibold">{labelPlural()}: </label> {compra?.UrlCertificados.length}
                </p>

                {compra?.UrlCertificados && compra.UrlCertificados.length > 0 && (
                    <div className="flex justify-center mb-4">
                        {
                            isAndroid && isInstagram ? (
                                <a
                                    href={`${window.location.origin}/compra/extrato-compras?token=${encodeURIComponent(usuario?.TokenAcesso!)}`}
                                    target="_blank"
                                    download
                                >
                                    <Button type="button" className="gap-2" >
                                        <Share2 size={16} />
                                        {
                                            compartilhando ? <span>Compartilhando...</span> : <span>Compartilhar {labelPlural()}</span>
                                        }
                                    </Button>
                                </a>
                            ) : (

                                <Button
                                    disabled={compartilhando}
                                    className="gap-2"
                                    onClick={handleCompartilharTitulos}
                                >
                                    <Share2 size={16} />
                                    {
                                        compartilhando ? <span>Compartilhando...</span> : <span>Compartilhar {labelPlural()}</span>
                                    }
                                </Button>
                            )
                        }
                    </div>
                )}

                <div className="mb-20 flex flex-col items-center justify-center">
                    {
                        compra?.UrlCertificados.map((img) => (
                            <Card className='border-2 mb-10'>
                                <CardContent className="p-3">
                                    <img src={img} />
                                </CardContent>
                                <Separator />
                            </Card>
                        ))
                    }
                    {
                        !compraRapida && (
                            <Button className="gap-2" onClick={() => {
                                navigate(-1);
                            }} >
                                <span>Voltar</span>
                            </Button>
                        )
                    }
                </div>
            </div>
        )
    )
}